'use client';

import { useRouter } from 'next/navigation';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Logo } from './Logo';
import { Starfield } from './Starfield';
import { config } from './config';
import styles from '../styles/Home.module.css';

export default function HomePage() {
  const router = useRouter();
  const [roomName, setRoomName] = useState(config.defaultRoom);
  const [pin, setPin] = useState('');
  const [editing, setEditing] = useState(false);
  const [jumping, setJumping] = useState(false);
  const [targetRoom, setTargetRoom] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const pinRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const saved = sessionStorage.getItem('room-pin');
    if (saved) setPin(saved);
  }, []);

  const joinRoom = () => {
    const room = roomName.trim() || config.defaultRoom;
    if (pin) sessionStorage.setItem('room-pin', pin);
    setTargetRoom(room);
    setJumping(true);
  };

  const onJumpComplete = useCallback(() => {
    router.push(`/rooms/${targetRoom}`);
  }, [router, targetRoom]);

  return (
    <>
      <Starfield jump={jumping} onJumpComplete={onJumpComplete} />
      <main
        className={styles.main}
        data-lk-theme="default"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div
          className="header"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            opacity: jumping ? 0 : 1,
            transition: 'opacity 0.5s',
          }}
        >
          <Logo />
          <h1
            style={{
              fontSize: '2.5rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            {config.name}
          </h1>
          <h2 style={{ opacity: 0.6, fontWeight: 400, margin: 0 }}>{config.subtitle}</h2>
        </div>
        <div
          className={styles.tabContainer}
          style={{
            opacity: jumping ? 0 : 1,
            transition: 'opacity 0.5s',
          }}
        >
          <div className={styles.tabContent}>
            {editing ? (
              <input
                ref={inputRef}
                type="text"
                placeholder="Raum"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setEditing(false);
                    joinRoom();
                  }
                  if (e.key === 'Escape') setEditing(false);
                }}
                onBlur={() => setEditing(false)}
                autoFocus
                disabled={jumping}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0',
                  fontSize: '1.1em',
                  color: 'var(--lk-fg)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setEditing(true);
                  setTimeout(() => inputRef.current?.select(), 0);
                }}
              >
                <span style={{ opacity: 0.5, fontSize: '0.85em' }}>Raum:</span>
                <span>{roomName || config.defaultRoom}</span>
                <span style={{ opacity: 0.4, fontSize: '0.85em' }}>&#9998;</span>
              </div>
            )}
            <input
              ref={pinRef}
              type="password"
              inputMode="numeric"
              placeholder="PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') joinRoom();
              }}
              disabled={jumping}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                fontSize: '16px',
                textAlign: 'center',
                letterSpacing: '0.3em',
                borderRadius: '0.5rem',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'var(--lk-fg)',
                marginTop: '0.5rem',
              }}
            />
            <button
              className="lk-button"
              style={{ width: '100%', marginTop: '0.5rem' }}
              onClick={joinRoom}
              disabled={jumping || !pin}
            >
              {config.joinButtonLabel}
            </button>
          </div>
        </div>
        <span
          title={process.env.NEXT_PUBLIC_BUILD_DATE}
          style={{
            position: 'fixed',
            bottom: '0.75rem',
            right: '1rem',
            fontSize: '0.75rem',
            opacity: jumping ? 0 : 0.3,
            transition: 'opacity 0.5s',
            color: 'var(--lk-fg)',
            fontFamily: 'monospace',
            cursor: 'default',
          }}
        >
          {process.env.NEXT_PUBLIC_GIT_SHA}
        </span>
      </main>
    </>
  );
}
